import { OktaAuth } from '@okta/okta-auth-js';
import { sendClientLog } from 'services/logService';
import { store } from 'configureStore';
import { setConnectionState } from 'store/actions';
import * as revueHubConnection from 'signalR/revueHubConnection';

const apiPath = process.env.REACT_APP_API_PATH;
const defaultRenewalRetryMaxCount = 2;
const CLIENT_ID = process.env.REACT_APP_OKTA_CLIENT_ID;
const ISSUER = process.env.REACT_APP_OKTA_ISSUER;
const REDIRECT_URI = `${window.location.origin}/login/callback`;

export const config = {
  clientId: CLIENT_ID,
  issuer: ISSUER,
  redirectUri: REDIRECT_URI,
  scopes: ['openid', 'profile', 'email', 'offline_access'],
  pkce: true
};

export const oktaAuth = new OktaAuth(config);
oktaAuth.start();
async function authFetchWithLog(url, options, shouldLogErrors = true) {
  const t0 = performance.now();
  let t1;
  try {
    const accessToken = oktaAuth.getAccessToken();
    let optionsWithAuth = {
      ...options,
      headers: {
        ...options?.headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
    const res = await fetch(`${apiPath}${url}`, optionsWithAuth);
    t1 = performance.now();
    if (res.status === 401) {
      console.log(res)
      if (store && store.getState().appState.connectionState !== 'expired') {
        store.dispatch(setConnectionState('expired'));
        revueHubConnection.connection.stop();
        console.log('Stopping revueHubConnection');
      }
    }
    if (res.status === 200) {
      if (store && store.getState().appState.connectionState === 'expired') {
        store.dispatch(setConnectionState('connected'));
        revueHubConnection.start();
        console.log('Starting revueHubConnection');
      }
    }
    return res;
  } catch (error) {
    t1 = performance.now();
    console.log(`Client API - authFetch error: ${error.message ?? error}`, url)
    if (shouldLogErrors) { // Should always normally log. Skip if this itself is a request to log that errored (see network.js)
      console.log('Logging above error...', url)
      sendClientLog('Error', `Client API - AuthFetch error: ${error.message ?? error}`);
    }
    console.log(error)
    throw new Error(error.message ?? error); // Throw error to be caught by the retry block in fetchWithToken
  } finally {
    const secElapsed = (t1 - t0) / 1000;
    const durationToLog = getDurationToLog(url, secElapsed);
    if (shouldLogErrors && durationToLog) { // Should always normally log. Skip if this itself is a request to log that errored (see network.js)
      sendClientLog('Warn', `Client API - Request to ${url} took over ${durationToLog} secs. Duration: ${(secElapsed).toFixed(3)}.${options?.body ? ` Body: ${options.body.slice(0, maxBodyLength - 1)}` : ''}`);
    }
  }
}

const maxBodyLength = 1000;
/// IsMatch method will add wildcard to the start, replace any * with wildcard, and replace any # with wildcard digits (for ids).
const ignoreMaxDurationRoutes = ['explore/GetClusters', 'revuehub*']; // Skip endpoints known to potentially have long durations
const maxDuration60Routes = ['postAcquisition/tasks/#/cma'];
const maxDuration30Routes = ['underwrite/bidSubmission/updateBidBatch', 'underwrite/import/opportunity/autoFill', 'underwrite/import/opportunity/validateGeo', 'underwrite/workflow/opportunity/reloadStatus'];
const maxDuration20Routes = ['postAcquisition/tasks/#/msrHomes', 'underwrite/import/opportunity', 'underwrite/prop/#/msrHomes'];
const maxDuration15Routes = ['explore/GetMarkets', 'media/images/#', 'media/property/images/#?&showListingHistory=true*', 'underwrite/bidSubmission/#/updateBidSingle', 'underwrite/prop/edit', 'underwrite/prop/markets/#', 'underwrite/prop/refreshModelTax', 'underwrite/workflow/opportunitygrid'];

const getDurationToLog = (url, duration) => {
  if (ignoreMaxDurationRoutes.some(route => isMatch(url, route))) return null;

  if (duration > 60 && maxDuration60Routes.some(route => isMatch(url, route)))
  {
      return 60;
  }
  else if (duration > 30 && maxDuration30Routes.some(route => isMatch(url, route)))
  {
      return 30;
  }
  else if (duration > 20 && maxDuration20Routes.some(route => isMatch(url, route)))
  {
      return 20;
  }
  else if (duration > 15 && maxDuration15Routes.some(route => isMatch(url, route)))
  {
      return 15;
  }
  else if (duration > 10 && !maxDuration60Routes.some(route => isMatch(url, route)) && !maxDuration30Routes.some(route => isMatch(url, route)) && !maxDuration20Routes.some(route => isMatch(url, route)) && !maxDuration15Routes.some(route => isMatch(url, route)))
  {
      return 10;
  }
  else
  {
      return null;
  }
}

const isMatch = (displayUrl, input) => {
  const pattern = input
  .replace(/\*/g, '.*') // Replace any * with .* which matches any quantity of any character
  .replace(/#/g, '\\d+'); // Replace any # with \d+ which matches 1+ quantity of digits 0-9 (to match ids in url)
  const regex = new RegExp(pattern);
  return regex.test(displayUrl);
}

export const fetchWithToken = async (url, options, shouldLogErrors = true, renewalRetryMaxCount = defaultRenewalRetryMaxCount) => {
  let renewalRetryCount = 0;
  function onError(error) {
    if (renewalRetryCount < renewalRetryMaxCount) {
      renewalRetryCount++;
      console.log(`Client API - Beginning retry attempt ${renewalRetryCount} for request to ${url}`)
      sendClientLog('Error', `Client API - Beginning retry attempt ${renewalRetryCount} for request to ${url}`); // Log the retry with count
      return authFetchWithLog(url, options, shouldLogErrors).catch(onError);
    } else {
      if (renewalRetryMaxCount > 0) console.log(`Exceeded max retries for error ${error.message}`, url)
      throw new Error(error.message ?? error); // If all retries fail, throw error to be caught by function that made the original request
    }
  }
  return authFetchWithLog(url, options, shouldLogErrors).catch(onError);
};
